// Recepción de mercadería (GRC) · docs/108 B6. Contra una OC emitida (las líneas pendientes vienen de
// la OC) o sin OC (con motivo obligatorio, D-98). Se guarda como borrador y CONFIRMAR es lo que entra
// al stock (una entrada por línea aceptada). Confirmada, solo se reversa con dos personas (D-100): una
// la pide aquí y otra la aprueba en la bandeja de aprobaciones de Compras.
import { useState } from 'react';
import { PanelError } from '../comun/Estados';
import { EncabezadoImpreso, Imprimir } from '../comun/Imprimir';
import { decimal, entradaDecimal, fecha, fechaHora, pesos, rut } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { SelectorBodega, useBodegas } from '../stock/PantallasStock';
import { BuscadorCliente } from '../ventas/BuscadorCliente';
import { BuscadorProducto } from '../ventas/BuscadorProducto';
import { DialogoFaltantes } from './Faltantes';
import { DialogoMotivo, Historia, NombreProveedor } from './comun';
import {
  AvisoDoc,
  CaminoDoc,
  CuerpoDoc,
  DatosDoc,
  EncabezadoDoc,
  Tarjeta,
  type Paso,
} from './FichaDocumento';
import {
  NOMBRE_ESTADO_RECEPCION,
  useAccionCompras,
  useOrdenCompra,
  useRecepcion,
  type RecepcionLeida,
} from './datos';

export interface AccionesRecepcion {
  alCreada: (recepcionId: string, titulo: string) => void;
  alAbrirOc: (ordenId: string, numero: string) => void;
  alFacturar: (recepcionId: string) => void;
  alDevolver: (recepcionId: string) => void;
}

export function Recepcion({
  recepcionId,
  ordenId,
  ...acciones
}: AccionesRecepcion & {
  recepcionId?: string | undefined;
  /** Nueva recepción CONTRA esta OC; sin ella (y sin id) es una recepción SIN OC. */
  ordenId?: string | undefined;
}) {
  const { empresa } = useSesion();
  const q = useRecepcion(empresa!.id, recepcionId ?? null);
  if (!recepcionId)
    return ordenId ? (
      <NuevaContraOc ordenId={ordenId} alCreada={acciones.alCreada} />
    ) : (
      <NuevaSinOc alCreada={acciones.alCreada} />
    );
  if (q.isError) return <PanelError error={q.error} />;
  if (!q.data) return <p className="apagado pantalla">Cargando recepción…</p>;
  return <FichaRecepcion r={q.data} {...acciones} />;
}

const comunes = (guiaTipo: string, guiaFolio: string, observaciones: string) => ({
  ...(guiaFolio.trim() ? { guia: { tipo: guiaTipo, folio: guiaFolio.trim() } } : {}),
  ...(observaciones.trim() ? { observaciones: observaciones.trim() } : {}),
});

function CamposGuia({
  tipo,
  folio,
  alTipo,
  alFolio,
}: {
  tipo: string;
  folio: string;
  alTipo: (v: string) => void;
  alFolio: (v: string) => void;
}) {
  return (
    <>
      <label className="campo">
        <span className="etiqueta">Documento que acompaña</span>
        <select
          aria-label="Tipo de documento"
          value={tipo}
          onChange={(e) => alTipo(e.target.value)}
        >
          <option value="GUIA_DESPACHO">Guía de despacho</option>
          <option value="FACTURA">Factura</option>
          <option value="OTRO">Otro</option>
        </select>
      </label>
      <label className="campo campo-corto">
        <span className="etiqueta">Folio</span>
        <input
          aria-label="Folio del documento"
          value={folio}
          onChange={(e) => alFolio(e.target.value)}
        />
      </label>
    </>
  );
}

// ================================================================ nueva contra OC

function NuevaContraOc({
  ordenId,
  alCreada,
}: {
  ordenId: string;
  alCreada: AccionesRecepcion['alCreada'];
}) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const oc = useOrdenCompra(empresaId, ordenId);
  const accion = useAccionCompras(empresaId);
  const [cant, setCant] = useState<
    Record<number, { recibida: string; rechazada: string; motivo: string }>
  >({});
  const [guiaTipo, setGuiaTipo] = useState('GUIA_DESPACHO');
  const [guiaFolio, setGuiaFolio] = useState('');
  const [observaciones, setObservaciones] = useState('');
  if (oc.isError) return <PanelError error={oc.error} />;
  if (!oc.data) return <p className="apagado pantalla">Cargando OC…</p>;
  const o = oc.data;
  const recibida = new Map(o.cumplimiento.lineas.map((l) => [l.numero, l]));
  /** Lo pendiente en la PRESENTACIÓN de la línea: (ordenada − recibida) / factor, nunca negativo. */
  const pendiente = (numero: number, factor: string) => {
    const c = recibida.get(numero);
    if (!c) return '0';
    const p = (Number(c.ordenadaBase) - Number(c.recibidaBase)) / Number(factor);
    return p > 0 ? String(Math.round(p * 10000) / 10000).replace('.', ',') : '0';
  };
  const valor = (n: number, factor: string) =>
    cant[n] ?? { recibida: pendiente(n, factor), rechazada: '', motivo: '' };
  const lineas = o.lineas
    .filter((l) => l.claseLinea === 'MERCADERIA')
    .map((l) => {
      const v = valor(l.numero, l.factorABase);
      return {
        l,
        v,
        api: {
          ordenLineaNumero: l.numero,
          cantidadRecibida: entradaDecimal(v.recibida || '0', 4),
          ...(v.rechazada.trim()
            ? { cantidadRechazada: entradaDecimal(v.rechazada, 4), motivoRechazo: v.motivo.trim() }
            : {}),
        },
      };
    });
  const conCantidad = lineas.filter(
    (x) => x.api.cantidadRecibida && x.api.cantidadRecibida !== '0',
  );
  const invalidas = lineas.filter(
    (x) =>
      !x.api.cantidadRecibida ||
      ('cantidadRechazada' in x.api &&
        (!x.api.cantidadRechazada || (x.api.motivoRechazo ?? '').length < 3)),
  ).length;
  const guardar = (confirmar: boolean) =>
    accion.mutate(
      {
        ruta: '/compras/recepciones',
        cuerpo: {
          modalidad: 'CONTRA_OC',
          ordenId,
          ...comunes(guiaTipo, guiaFolio, observaciones),
          lineas: conCantidad.map((x) => x.api),
        },
      },
      {
        onSuccess: (r) => {
          const id = (r as { recepcionId: string }).recepcionId;
          if (!confirmar) return alCreada(id, `Recepción ${o.numeroVisible}`);
          accion.mutate(
            { ruta: `/compras/recepciones/${id}/confirmar`, cuerpo: { version: 1 } },
            {
              onSuccess: (c) =>
                alCreada(id, (c as { numeroVisible?: string }).numeroVisible ?? 'Recepción'),
              onError: () => alCreada(id, `Recepción ${o.numeroVisible}`),
            },
          );
        },
      },
    );
  return (
    <div className="pantalla documento-compra" data-pantalla="recepcion-nueva">
      <div className="encabezado-entidad">
        <span className="ruta-migas">Compras › Recepción de mercadería</span>
        <h2>Recibir contra {o.numeroVisible}</h2>
        <p>
          {o.orden.proveedor_razon_social} · {rut(o.orden.proveedor_rut)}
        </p>
      </div>
      <div className="barra-filtros">
        <CamposGuia tipo={guiaTipo} folio={guiaFolio} alTipo={setGuiaTipo} alFolio={setGuiaFolio} />
      </div>
      <table className="grilla compacta" aria-label="Líneas a recibir">
        <thead>
          <tr>
            <th style={{ width: '3rem' }}>N°</th>
            <th style={{ width: '8rem' }}>SKU</th>
            <th>Descripción</th>
            <th>Present.</th>
            <th className="der">Pedido</th>
            <th className="der">Pendiente</th>
            <th className="der">Recibido</th>
            <th className="der">Rechazado</th>
            <th>Motivo del rechazo</th>
          </tr>
        </thead>
        <tbody>
          {lineas.map(({ l, v }) => {
            const set = (campo: 'recibida' | 'rechazada' | 'motivo', x: string) =>
              setCant((c) => ({ ...c, [l.numero]: { ...v, [campo]: x } }));
            return (
              <tr key={l.id}>
                <td>{l.numero}</td>
                <td className="mono">{l.sku}</td>
                <td>{l.descripcion}</td>
                <td>{l.presentacionCodigo}</td>
                <td className="der">{decimal(l.cantidad)}</td>
                <td className="der">{pendiente(l.numero, l.factorABase)}</td>
                <td className="der">
                  <input
                    className="entrada-numero"
                    aria-label={`Recibido línea ${l.numero}`}
                    value={v.recibida}
                    onChange={(e) => set('recibida', e.target.value)}
                  />
                </td>
                <td className="der">
                  <input
                    className="entrada-numero"
                    aria-label={`Rechazado línea ${l.numero}`}
                    value={v.rechazada}
                    onChange={(e) => set('rechazada', e.target.value)}
                  />
                </td>
                <td>
                  <input
                    aria-label={`Motivo rechazo línea ${l.numero}`}
                    disabled={!v.rechazada.trim()}
                    value={v.motivo}
                    onChange={(e) => set('motivo', e.target.value)}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <label className="campo">
        <span className="etiqueta">Observaciones</span>
        <textarea
          aria-label="Observaciones"
          rows={2}
          value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
        />
      </label>
      {invalidas > 0 && (
        <p className="tono-malo">
          Revisa las cantidades (hasta 4 decimales) y el motivo de cada rechazo.
        </p>
      )}
      {accion.isError && <PanelError error={accion.error} />}
      <div className="acciones-od">
        <button
          type="button"
          disabled={!conCantidad.length || invalidas > 0 || accion.isPending}
          onClick={() => guardar(false)}
        >
          Guardar borrador
        </button>
        <button
          type="button"
          className="primario"
          disabled={!conCantidad.length || invalidas > 0 || accion.isPending}
          onClick={() => guardar(true)}
        >
          Confirmar recepción (entra al stock)
        </button>
      </div>
    </div>
  );
}

// ================================================================ nueva sin OC

interface LineaSinOc {
  clave: string;
  productoId: string;
  presentacionId: string;
  sku: string;
  nombre: string;
  presentacion: string;
  cantidad: string;
}

function NuevaSinOc({ alCreada }: { alCreada: AccionesRecepcion['alCreada'] }) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const bodegas = useBodegas(empresaId);
  const accion = useAccionCompras(empresaId);
  const [proveedor, setProveedor] = useState<{ id: string; razonSocial: string } | null>(null);
  const [bodega, setBodega] = useState('');
  const [motivo, setMotivo] = useState('');
  const [guiaTipo, setGuiaTipo] = useState('GUIA_DESPACHO');
  const [guiaFolio, setGuiaFolio] = useState('');
  const [lineas, setLineas] = useState<LineaSinOc[]>([]);
  const [buscandoProv, setBuscandoProv] = useState(false);
  const [buscandoProd, setBuscandoProd] = useState(false);
  const bodegaEfectiva = bodega || bodegas.lista[0]?.id || '';
  const api = lineas.map((l) => ({
    productoId: l.productoId,
    presentacionId: l.presentacionId,
    cantidadRecibida: entradaDecimal(l.cantidad, 4),
  }));
  const valido =
    !!proveedor &&
    !!bodegaEfectiva &&
    motivo.trim().length >= 10 &&
    api.length > 0 &&
    api.every((l) => l.cantidadRecibida && l.cantidadRecibida !== '0');
  const guardar = (confirmar: boolean) =>
    accion.mutate(
      {
        ruta: '/compras/recepciones',
        cuerpo: {
          modalidad: 'SIN_OC',
          empresaId,
          proveedorEntidadId: proveedor!.id,
          ubicacionId: bodegaEfectiva,
          motivoSinOc: motivo.trim(),
          ...comunes(guiaTipo, guiaFolio, ''),
          lineas: api,
        },
      },
      {
        onSuccess: (r) => {
          const id = (r as { recepcionId: string }).recepcionId;
          if (!confirmar) return alCreada(id, 'Recepción sin OC');
          accion.mutate(
            { ruta: `/compras/recepciones/${id}/confirmar`, cuerpo: { version: 1 } },
            {
              onSuccess: (c) =>
                alCreada(id, (c as { numeroVisible?: string }).numeroVisible ?? 'Recepción'),
              onError: () => alCreada(id, 'Recepción sin OC'),
            },
          );
        },
      },
    );
  return (
    <div className="pantalla documento-compra" data-pantalla="recepcion-sin-oc">
      <div className="encabezado-entidad">
        <span className="ruta-migas">Compras › Recepción de mercadería</span>
        <h2>Recepción sin orden de compra</h2>
        <p className="apagado">
          Excepción: queda el motivo y, según la política, puede requerir aprobación de otra
          persona.
        </p>
      </div>
      <div className="barra-filtros">
        <div className="campo">
          <span className="etiqueta">Proveedor</span>
          <button
            type="button"
            className="selector-cliente"
            aria-label="Elegir proveedor"
            onClick={() => setBuscandoProv(true)}
          >
            {proveedor ? proveedor.razonSocial : 'Buscar proveedor…'}
          </button>
        </div>
        <SelectorBodega bodegas={bodegas.lista} valor={bodegaEfectiva} alCambiar={setBodega} />
        <CamposGuia tipo={guiaTipo} folio={guiaFolio} alTipo={setGuiaTipo} alFolio={setGuiaFolio} />
      </div>
      <label className="campo">
        <span className="etiqueta">Motivo sin OC (mínimo 10 caracteres)</span>
        <input
          aria-label="Motivo sin OC"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
        />
      </label>
      <table className="grilla compacta" aria-label="Líneas recibidas">
        <thead>
          <tr>
            <th style={{ width: '8rem' }}>SKU</th>
            <th>Producto</th>
            <th>Present.</th>
            <th className="der">Cantidad</th>
            <th style={{ width: '3rem' }} />
          </tr>
        </thead>
        <tbody>
          {lineas.map((l, i) => (
            <tr key={l.clave}>
              <td className="mono">{l.sku}</td>
              <td>{l.nombre}</td>
              <td>{l.presentacion}</td>
              <td className="der">
                <input
                  className="entrada-numero"
                  aria-label={`Cantidad línea ${i + 1}`}
                  value={l.cantidad}
                  onChange={(e) =>
                    setLineas((ls) =>
                      ls.map((x) => (x.clave === l.clave ? { ...x, cantidad: e.target.value } : x)),
                    )
                  }
                />
              </td>
              <td>
                <button
                  type="button"
                  className="boton-mini"
                  aria-label={`Quitar línea ${i + 1}`}
                  onClick={() => setLineas((ls) => ls.filter((x) => x.clave !== l.clave))}
                >
                  ✕
                </button>
              </td>
            </tr>
          ))}
          {!lineas.length && (
            <tr>
              <td colSpan={5} className="grilla-vacia">
                Agrega lo recibido con «Agregar producto».
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <div className="barra-filtros">
        <button type="button" onClick={() => setBuscandoProd(true)}>
          Agregar producto
        </button>
      </div>
      {accion.isError && <PanelError error={accion.error} />}
      <div className="acciones-od">
        <button type="button" disabled={!valido || accion.isPending} onClick={() => guardar(false)}>
          Guardar borrador
        </button>
        <button
          type="button"
          className="primario"
          disabled={!valido || accion.isPending}
          onClick={() => guardar(true)}
        >
          Confirmar recepción (entra al stock)
        </button>
      </div>
      {buscandoProv && (
        <BuscadorCliente
          empresaId={empresaId}
          rol="proveedor"
          alElegir={(c) => {
            setProveedor({ id: c.id, razonSocial: c.razon_social });
            setBuscandoProv(false);
          }}
          alCerrar={() => setBuscandoProv(false)}
        />
      )}
      {buscandoProd && (
        <BuscadorProducto
          empresaId={empresaId}
          listaId={null}
          ubicacionId={bodegaEfectiva}
          ubicaciones={bodegas.lista}
          alElegir={(p) => {
            setBuscandoProd(false);
            setLineas((ls) => [
              ...ls,
              {
                clave: `${p.producto.id}:${Date.now()}`,
                productoId: p.producto.id,
                presentacionId: p.presentacion.id,
                sku: p.producto.sku,
                nombre: p.producto.nombre,
                presentacion: p.presentacion.codigo,
                cantidad: '1',
              },
            ]);
          }}
          alCerrar={() => setBuscandoProd(false)}
        />
      )}
    </div>
  );
}

// ================================================================ ficha

const FACTURACION: Record<string, string> = {
  SIN_FACTURA: 'Sin factura',
  PARCIAL: 'Facturada parcial',
  COMPLETA: 'Facturada',
};

function FichaRecepcion({
  r,
  alAbrirOc,
  alFacturar,
  alDevolver,
}: AccionesRecepcion & { r: RecepcionLeida }) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const bodegas = useBodegas(empresaId);
  const accion = useAccionCompras(empresaId);
  const oc = useOrdenCompra(empresaId, r.borrador.orden_id);
  const [dialogo, setDialogo] = useState<'descartar' | 'reversar' | 'faltantes' | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [imprimir, setImprimir] = useState(false);
  const b = r.borrador;
  const bodega = bodegas.lista.find((x) => x.id === b.ubicacion_id);
  const facturacion = new Map(r.facturacion.map((f) => [f.recepcionLineaId, f]));
  const reversaPendiente = r.solicitudesReversa.find((s) => s.estado === 'PENDIENTE');
  const titulo = r.numeroVisible ?? 'Recepción (borrador)';

  const confirmar = () =>
    accion.mutate({
      ruta: `/compras/recepciones/${b.id}/confirmar`,
      cuerpo: { version: b.version },
    });

  const estados = r.lineas.map((l) => facturacion.get(l.id)?.estado ?? 'SIN_FACTURA');
  const facturada = estados.length > 0 && estados.every((e) => e === 'COMPLETA');
  const facturaParcial = !facturada && estados.some((e) => e !== 'SIN_FACTURA');
  const confirmada = r.estado !== 'BORRADOR';
  const pasos: Paso[] = [
    {
      texto: 'Borrador',
      estado: confirmada ? 'hecho' : 'actual',
      ayuda: confirmada ? undefined : 'revisa y confirma',
    },
    {
      texto: 'Confirmada · entra al stock',
      estado: confirmada ? 'hecho' : 'pendiente',
      ayuda: r.recepcion?.confirmada_en ? fechaHora(r.recepcion.confirmada_en) : undefined,
    },
    {
      texto: 'Facturada',
      estado: facturada ? 'hecho' : facturaParcial ? 'aviso' : confirmada ? 'actual' : 'pendiente',
      ayuda: facturada
        ? 'la factura ya la cubre'
        : facturaParcial
          ? 'parte sin factura'
          : 'esperando la factura del proveedor',
    },
  ];
  const acciones = (
    <>
      {r.estado === 'BORRADOR' && puede('compras.recepcion.confirmar') && (
        <button type="button" className="primario" disabled={accion.isPending} onClick={confirmar}>
          Confirmar recepción (entra al stock)
        </button>
      )}
      {r.estado === 'BORRADOR' && (
        <button type="button" onClick={() => setDialogo('descartar')}>
          Descartar borrador
        </button>
      )}
      {r.estado === 'CONFIRMADA' && puede('compras.factura.crear') && (
        <button type="button" className="primario" onClick={() => alFacturar(b.id)}>
          Registrar factura del proveedor
        </button>
      )}
      {r.estado === 'CONFIRMADA' &&
        puede('compras.devolucion.crear') &&
        puede('compras.devolucion.confirmar') && (
          <button type="button" onClick={() => setDialogo('faltantes')}>
            Faltantes y devoluciones
          </button>
        )}
      {r.estado === 'CONFIRMADA' && puede('compras.devolucion.crear') && (
        <button type="button" onClick={() => alDevolver(b.id)}>
          Devolver al proveedor (con aprobación)
        </button>
      )}
      {r.estado === 'CONFIRMADA' && !reversaPendiente && puede('compras.recepcion.reversar') && (
        <button type="button" onClick={() => setDialogo('reversar')}>
          Solicitar reversa
        </button>
      )}
      {r.recepcion && (
        <button type="button" onClick={() => setImprimir(true)}>
          Imprimir recepción
        </button>
      )}
    </>
  );

  return (
    <div className="pantalla documento-compra doc-ficha" data-pantalla="recepcion-ficha">
      <EncabezadoDoc
        sigla="GRC"
        migas="Compras › Guía de recepción"
        titulo={titulo}
        insignias={
          <>
            <span
              className={`insignia ${r.estado === 'CONFIRMADA' ? 'tono-ok' : r.estado === 'BORRADOR' ? 'tono-info' : 'tono-neutro'}`}
              data-estado={r.estado}
            >
              {NOMBRE_ESTADO_RECEPCION[r.estado]}
            </span>
            {reversaPendiente && <span className="insignia tono-aviso">Reversa solicitada</span>}
          </>
        }
        sub={
          <>
            <b>
              {r.recepcion?.proveedor_razon_social ?? (
                <NombreProveedor id={b.proveedor_entidad_id} />
              )}
            </b>{' '}
            · {bodega ? bodega.nombre : 'bodega'} · {fecha(b.fecha_negocio)}
          </>
        }
        acciones={acciones}
      />
      <CaminoDoc pasos={pasos} />
      {aviso && <AvisoDoc tono="ok">{aviso}</AvisoDoc>}
      {b.motivo_sin_oc && (
        <AvisoDoc tono="info">
          <b>Sin OC:</b> {b.motivo_sin_oc}
        </AvisoDoc>
      )}
      {r.excepciones.length > 0 && (
        <AvisoDoc tono="aviso">
          Excepciones:{' '}
          {r.excepciones
            .map((x) => `${x.tipo.toLowerCase().replace(/_/g, ' ')} (${x.estado.toLowerCase()})`)
            .join(' · ')}
        </AvisoDoc>
      )}
      {accion.isError && !dialogo && (
        <div className="doc-aviso tono-malo">
          <PanelError error={accion.error} />
        </div>
      )}
      <CuerpoDoc
        principal={
          <>
            <Tarjeta
              titulo={r.recepcion ? 'Productos recibidos' : 'Productos del borrador'}
              extra={`${r.recepcion ? r.lineas.length : r.lineasBorrador.length} líneas`}
            >
              {r.recepcion ? (
                <table className="grilla compacta" aria-label="Líneas recibidas">
                  <thead>
                    <tr>
                      <th>N°</th>
                      <th>SKU</th>
                      <th>Descripción</th>
                      <th>Present.</th>
                      <th className="der">Recibido</th>
                      <th className="der">Rechazado</th>
                      <th className="der">Aceptado</th>
                      <th className="der">Precio OC</th>
                      <th>Factura</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.lineas.map((l) => (
                      <tr key={l.id}>
                        <td>{l.numero}</td>
                        <td className="mono">{l.sku}</td>
                        <td>{l.descripcion}</td>
                        <td>{l.presentacion_codigo}</td>
                        <td className="der">{decimal(l.cantidad_recibida)}</td>
                        <td className="der">
                          {Number(l.cantidad_rechazada) ? decimal(l.cantidad_rechazada) : ''}
                        </td>
                        <td className="der">
                          <b>{decimal(l.cantidad_aceptada)}</b>
                        </td>
                        <td className="der">{l.precio_oc ? pesos(l.precio_oc) : '—'}</td>
                        <td>{FACTURACION[facturacion.get(l.id)?.estado ?? 'SIN_FACTURA']}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="grilla compacta" aria-label="Líneas del borrador">
                  <thead>
                    <tr>
                      <th>N°</th>
                      <th>Línea OC</th>
                      <th className="der">Recibido</th>
                      <th className="der">Rechazado</th>
                      <th>Motivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.lineasBorrador.map((l) => {
                      const ol = oc.data?.lineas.find((x) => x.numero === l.orden_linea_numero);
                      return (
                        <tr key={l.id}>
                          <td>{l.numero}</td>
                          <td>
                            {ol
                              ? `${ol.numero} · ${ol.sku} ${ol.descripcion}`
                              : (l.orden_linea_numero ?? 'sin OC')}
                          </td>
                          <td className="der">{decimal(l.cantidad_recibida)}</td>
                          <td className="der">{decimal(l.cantidad_rechazada)}</td>
                          <td>{l.motivo_rechazo ?? ''}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </Tarjeta>
            <Tarjeta titulo="Historia" etiqueta="Historia">
              <Historia transiciones={r.transiciones} />
            </Tarjeta>
          </>
        }
        lateral={
          <Tarjeta titulo="Datos de la recepción" etiqueta="Datos de la recepción">
            <DatosDoc
              datos={[
                {
                  e: 'Proveedor',
                  v: r.recepcion?.proveedor_razon_social ?? (
                    <NombreProveedor id={b.proveedor_entidad_id} />
                  ),
                },
                { e: 'Modalidad', v: b.modalidad === 'CONTRA_OC' ? 'Contra OC' : 'Sin OC' },
                {
                  e: 'OC',
                  v:
                    b.orden_id && oc.data ? (
                      <button
                        type="button"
                        className="enlace"
                        onClick={() => alAbrirOc(b.orden_id!, oc.data!.numeroVisible)}
                      >
                        {oc.data.numeroVisible}
                      </button>
                    ) : (
                      '—'
                    ),
                },
                { e: 'Bodega', v: bodega ? `${bodega.codigo} · ${bodega.nombre}` : '…' },
                { e: 'Fecha', v: fecha(b.fecha_negocio) },
                {
                  e: 'Documento',
                  v: b.guia_folio
                    ? `${b.guia_tipo === 'FACTURA' ? 'Factura' : 'Guía'} ${b.guia_folio}`
                    : '—',
                },
                { e: 'Confirmada', v: fechaHora(r.recepcion?.confirmada_en) },
              ]}
            />
          </Tarjeta>
        }
      />
      {dialogo === 'faltantes' && (
        <DialogoFaltantes
          empresaId={empresaId}
          recepcionId={b.id}
          titulo={titulo}
          lineas={r.lineas}
          alCerrar={() => setDialogo(null)}
          alListo={(n) => {
            setDialogo(null);
            setAviso(
              `Listo${n ? ` (${n})` : ''}: lo que faltó salió del stock y queda esperando la nota de crédito del proveedor.`,
            );
          }}
        />
      )}
      {dialogo === 'descartar' && (
        <DialogoMotivo
          titulo="Descartar borrador de recepción"
          textoBoton="Descartar"
          pendiente={accion.isPending}
          error={accion.isError ? accion.error : null}
          alConfirmar={(motivo) =>
            accion.mutate(
              {
                ruta: `/compras/recepciones/${b.id}/descartar`,
                cuerpo: { version: b.version, motivo },
              },
              { onSuccess: () => setDialogo(null) },
            )
          }
          alCerrar={() => setDialogo(null)}
        />
      )}
      {dialogo === 'reversar' && (
        <DialogoMotivo
          titulo={`Solicitar reversa de ${titulo}`}
          minimo={10}
          explicacion="La reversa saca del stock lo que entró. La pides tú y la aprueba OTRA persona (bandeja de aprobaciones de Compras)."
          textoBoton="Solicitar reversa"
          pendiente={accion.isPending}
          error={accion.isError ? accion.error : null}
          alConfirmar={(motivo) =>
            accion.mutate(
              { ruta: `/compras/recepciones/${b.id}/reversar`, cuerpo: { motivo } },
              { onSuccess: () => setDialogo(null) },
            )
          }
          alCerrar={() => setDialogo(null)}
        />
      )}
      {imprimir && r.recepcion && (
        <Imprimir alTerminar={() => setImprimir(false)}>
          <RecepcionImpresa
            r={r}
            empresa={empresa!.razon_social}
            bodega={bodega?.nombre ?? ''}
            oc={oc.data?.numeroVisible ?? null}
          />
        </Imprimir>
      )}
    </div>
  );
}

export function RecepcionImpresa({
  r,
  empresa,
  bodega,
  oc,
}: {
  r: RecepcionLeida;
  empresa: string;
  bodega: string;
  oc: string | null;
}) {
  return (
    <article className="impreso">
      <EncabezadoImpreso
        empresa={empresa}
        titulo="Recepción de mercadería"
        numero={r.numeroVisible ?? ''}
        detalle={
          <dl className="impreso-datos">
            <dt>Proveedor</dt>
            <dd>
              {r.recepcion?.proveedor_razon_social} · {rut(r.recepcion?.proveedor_rut)}
            </dd>
            <dt>Bodega</dt>
            <dd>{bodega}</dd>
            <dt>OC</dt>
            <dd>{oc ?? 'sin OC'}</dd>
            <dt>Documento</dt>
            <dd>{r.borrador.guia_folio ?? '—'}</dd>
          </dl>
        }
      />
      <table className="impreso-tabla">
        <thead>
          <tr>
            <th>Código</th>
            <th>Descripción</th>
            <th>Unidad</th>
            <th className="der">Recibido</th>
            <th className="der">Rechazado</th>
            <th className="der">Aceptado</th>
          </tr>
        </thead>
        <tbody>
          {r.lineas.map((l) => (
            <tr key={l.id}>
              <td>{l.sku}</td>
              <td>{l.descripcion}</td>
              <td>{l.presentacion_codigo}</td>
              <td className="der">{decimal(l.cantidad_recibida)}</td>
              <td className="der">{decimal(l.cantidad_rechazada)}</td>
              <td className="der">{decimal(l.cantidad_aceptada)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p style={{ marginTop: '3rem' }}>Recibido por: ______________________ Firma: ____________</p>
    </article>
  );
}
